import {
  ActionBar,
  ActionInfo,
  Button,
  Card,
  Check,
  Field,
  Loading,
  Modal,
  formatDate,
  formatMoney,
  useToast,
  ErrorNote,
} from "@corecrm/ui";
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fileUrl } from "../api/client";
import {
  useAcceptQuote,
  useDeclineQuote,
  useImpersonation,
  useMe,
  usePriceQuote,
  useQuote,
  useQuoteVersions,
  useViewHeartbeat,
} from "../api/queries";
import type { Quote, QuoteSummary } from "../api/types";

/** What changed in this version, when CoreNode chose to say (UX-112). */
export function RevisionNote({ quote }: { quote: Pick<QuoteSummary, "version_number" | "revision_note"> }) {
  return (
    <div className="note" role="note">
      <strong>What&rsquo;s new in version {quote.version_number}:</strong> {quote.revision_note}
    </div>
  );
}
import { Crumb, LoadError, ProjectLink, Status } from "../components/bits";
import { QuoteDocument } from "../components/QuoteDocument";
import type { Choosing } from "../components/QuoteDocument";
import { chosenNames, initialChoices } from "../lib/quoteOptions";

/* A quote or change request: the document, then (for the current version, while it's out with
   the client) the typed-signature acceptance, then earlier versions. Opening it marks it Viewed
   (the API does that). The prototype's pQuote(). */
export function QuotePage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const quote = useQuote(id, params.get("via"));
  if (quote.isPending) return <Loading />;
  if (quote.error)
    return (
      <LoadError
        error={quote.error}
        onRetry={() => quote.refetch()}
        what="quote"
        back={["/quotes", "Back to quotes"]}
      />
    );
  return <QuoteView key={quote.data.id} quote={quote.data} />;
}

function QuoteView({ quote: sent }: { quote: Quote }) {
  const options = useOptions(sent);
  // While the client chooses, the document, totals and schedule are the quote priced with their picks.
  const q = options.choosing ? options.priced : sent;
  useViewHeartbeat(sent.id, sent.view_id);
  const versions = useQuoteVersions(q.id);
  const panel = useRef<HTMLElement>(null);
  const cr = q.kind === "change_request";
  // UX-192: only billing contacts see amounts; others get a change request's scope without them.
  const amounts = q.total !== null;
  const pdf = fileUrl(`/quotes/${q.id}/pdf`);
  const current = versions.data?.at(-1);
  const replaced = !q.is_primary && current && current.id !== q.id ? current : null;
  const history = (versions.data ?? []).filter((v) => v.id !== q.id).reverse();

  const toPanel = () => {
    panel.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    panel.current?.classList.add("flash");
    setTimeout(() => panel.current?.classList.remove("flash"), 900);
    panel.current?.querySelector<HTMLInputElement>("input[type=text]")?.focus({ preventScroll: true });
  };

  return (
    <>
      <div className="row between">
        <Crumb to="/quotes" label="Quotes">
          {q.number} v{q.version_number}
        </Crumb>
        <div className="row">
          {amounts && (
            <a className="btn sm" href={pdf} target="_blank" rel="noopener">
              Preview PDF
            </a>
          )}
          {amounts && (
            <a className="btn sm" href={fileUrl(`/quotes/${q.id}/pdf`, true)} download>
              Download
            </a>
          )}
          {amounts && q.kind === "maintenance" && (
            <a
              className="btn sm"
              href={fileUrl(`/quotes/${q.id}/rate-card/pdf`)}
              target="_blank"
              rel="noopener"
            >
              Rate card
            </a>
          )}
        </div>
      </div>

      {replaced && (
        <div className="note acc" role="status">
          This version was replaced by v{replaced.version_number}.{" "}
          <Link to={`/quotes/${replaced.id}`}>View version {replaced.version_number}</Link>
        </div>
      )}
      {q.project_id && <ProjectNote quote={q} projectId={q.project_id} />}
      {q.revision_note && <RevisionNote quote={q} />}

      {/* While it can be accepted, its terms sit in the acceptance panel, next to the checkbox. */}
      <QuoteDocument quote={q} showTerms={!q.can_respond} choosing={options.choosing} />
      {options.pending && (
        <p className="faint" role="status">
          Updating the totals…
        </p>
      )}
      {options.error != null && <ErrorNote error={options.error} />}

      {q.can_respond && <AcceptPanel quote={q} panelRef={panel} options={options} />}

      {history.length > 0 && (
        <Card title="Version history" id="p-versions">
          <div className="item-list">
            {history.map((v) => (
              <div className="item" key={v.id}>
                <div className="what">
                  <strong>
                    Version {v.version_number}
                    {v.id === current?.id ? " · current" : ""}
                  </strong>
                  <span>
                    <Status status={v.status} /> · sent {formatDate(v.sent_at)} · {formatMoney(v.total)}
                  </span>
                  {v.revision_note && <span className="faint">{v.revision_note}</span>}
                </div>
                <div className="row">
                  <a className="btn sm" href={fileUrl(`/quotes/${v.id}/pdf`)} target="_blank" rel="noopener">
                    PDF
                  </a>
                  <Link className="btn sm" to={`/quotes/${v.id}`}>
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {q.can_respond ? (
        <ActionBar
          info={
            <ActionInfo
              label={cr ? "Change adds" : q.kind === "time_and_materials" ? "Estimated total" : "Total"}
              value={formatMoney(q.total)}
              sub={
                q.monthly_total && q.monthly_total.amount_minor > 0
                  ? `+ ${formatMoney(q.monthly_total)}/mo`
                  : undefined
              }
            />
          }
        >
          <a className="btn" href={pdf} target="_blank" rel="noopener">
            PDF
          </a>
          <Button variant="primary" onClick={toPanel}>
            {cr ? "Accept change" : "Accept quote"}
          </Button>
        </ActionBar>
      ) : (
        <ActionBar
          info={
            amounts ? undefined : (
              <span className="muted">
                Amounts are shown only to your account's billing contacts. Ask one of them to approve it.
              </span>
            )
          }
        >
          {amounts && (
            <a className="btn" href={pdf} target="_blank" rel="noopener">
              Preview PDF
            </a>
          )}
          {replaced && (
            <Link className="btn primary" to={`/quotes/${replaced.id}`}>
              View v{replaced.version_number}
            </Link>
          )}
        </ActionBar>
      )}
    </>
  );
}

/** The client's picks among the quote's options (UX-100) while they can accept it, and the quote
    priced with them. Each change asks the API for the new totals; an answer to an older pick is
    dropped, and until one comes back the document shows the picks against the last prices. */
function useOptions(q: Quote) {
  const price = usePriceQuote(q.id);
  const [chosen, setChosen] = useState(() => initialChoices(q));
  const [priced, setPriced] = useState(q);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const latest = useRef(0);
  const live = q.can_respond && q.has_options;

  const onChoose = (next: Set<string>) => {
    setChosen(next);
    setPending(true);
    const asked = ++latest.current;
    price.mutateAsync([...next]).then(
      (r) => {
        if (asked !== latest.current) return;
        setPriced(r);
        setError(null);
        setPending(false);
      },
      (e: unknown) => {
        if (asked !== latest.current) return;
        setError(e);
        setPending(false);
      },
    );
  };

  const choosing: Choosing | undefined = live ? { chosen, onChoose } : undefined;
  return {
    choosing,
    priced,
    pending,
    error,
    /** What accepting sends: left out when there's nothing to choose (the quote as sent). */
    choices: live ? [...chosen] : undefined,
    names: live ? chosenNames(q, chosen) : [],
  };
}

/** How the quote relates to its project (the prototype's cq-banner): the project a change
    request adds to or a renewal moves on, or the one an accepted quote started. */
function ProjectNote({ quote: q, projectId }: { quote: Quote; projectId: string }) {
  const link = <ProjectLink id={projectId} />;
  const accepted = q.status === "accepted";
  const agreement = q.kind === "maintenance" || q.kind === "hosting";
  let text: ReactNode;
  if (q.kind === "change_request")
    text = accepted ? (
      <>This change was added to your project {link}.</>
    ) : (
      <>
        This change request adds to your existing project {link}. Accepting it doesn&apos;t start a new
        project.
      </>
    );
  else if (agreement)
    text = accepted ? (
      <>This agreement covers your project {link}.</>
    ) : (
      <>This renews the agreement on your project {link}.</>
    );
  else text = <>This quote started your project {link}.</>;
  return (
    <div className="note acc" id="project-note">
      {text}
    </div>
  );
}

/** What accepting does, by quote kind (TDD §5: acceptance; §6: agreements). */
const ON_ACCEPT: Record<string, string> = {
  standard: "On acceptance we'll start the project and issue the first invoice in the payment schedule.",
  change_request:
    "On acceptance the change is added to your existing project and any payment due on acceptance is invoiced. No new project is started.",
  maintenance:
    "On acceptance your agreement starts on its effective date and is billed in advance, as the terms set out.",
  hosting:
    "On acceptance your agreement starts on its effective date and is billed in advance, as the terms set out.",
};

const ACCEPTED: Record<string, string> = {
  standard: "Quote accepted. Your project is set up.",
  change_request: "Change accepted. It's been added to your project.",
  maintenance: "Agreement accepted.",
  hosting: "Agreement accepted.",
};

function AcceptPanel({
  quote: q,
  panelRef,
  options,
}: {
  quote: Quote;
  panelRef: React.RefObject<HTMLElement | null>;
  options: ReturnType<typeof useOptions>;
}) {
  const me = useMe().data;
  const impersonation = useImpersonation().data;
  // An administrator logged in as the client can't sign for them in production (UX-152).
  const blocked = !!impersonation && !impersonation.binding_actions_allowed;
  const accept = useAcceptQuote(q.id);
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [declining, setDeclining] = useState(false);
  const cr = q.kind === "change_request";
  const agreement = q.kind === "maintenance" || q.kind === "hosting";
  const what = cr ? "change request" : agreement ? "agreement" : "quote";

  const submit = () =>
    accept.mutate(
      {
        signer_name: name.trim(),
        accept_terms: agreed,
        ...(options.choices && { choices: options.choices }),
      },
      {
        onSuccess: (r) => {
          toast(ACCEPTED[q.kind] ?? ACCEPTED.standard!);
          navigate(`/projects/${r.project_id}`);
        },
      },
    );

  return (
    <section
      className="accept"
      id="accept-panel"
      ref={panelRef}
      aria-label={cr ? "Accept change request" : "Accept quote"}
    >
      <h2>Accept this {what}</h2>
      <p className="muted">
        Typing your full name acts as your electronic signature. {ON_ACCEPT[q.kind] ?? ON_ACCEPT.standard}
      </p>
      {q.terms && (
        <section aria-labelledby="accept-terms-h" className="stack" style={{ gap: 6 }}>
          <h3 id="accept-terms-h">Terms and conditions</h3>
          <div
            className="note"
            tabIndex={0}
            aria-labelledby="accept-terms-h"
            style={{ maxHeight: 260, overflowY: "auto", whiteSpace: "pre-line", fontSize: 13 }}
          >
            {q.terms}
          </div>
        </section>
      )}
      <Field label="Full name">
        <input
          type="text"
          autoComplete="name"
          placeholder={me?.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Check checked={agreed} onChange={setAgreed}>
        {q.terms
          ? `I've read this ${what} and agree to the terms and conditions above${agreement ? "" : " and its payment schedule"}.`
          : `I've read this ${what} and agree to it${agreement ? "" : " and its payment schedule"}.`}
      </Check>
      {options.choosing && (
        <p className="qd-summary" aria-live="polite">
          {options.names.length
            ? `You've chosen: ${options.names.join(", ")}.`
            : "You haven't added any options."}
        </p>
      )}
      {blocked && (
        <p className="note" role="note">
          You&rsquo;re logged in as this client. Only they can accept and sign; you can view everything else.
        </p>
      )}
      {accept.error && <ErrorNote error={accept.error} />}
      <div className="row">
        <Button
          variant="primary"
          busy={accept.isPending}
          // Picks the API turned down can't be accepted either; the error says why.
          disabled={!name.trim() || !agreed || options.error != null || blocked}
          onClick={submit}
        >
          {cr ? "Accept change" : "Accept quote"}
        </Button>
        <Button onClick={() => setDeclining(true)}>Decline…</Button>
      </div>
      {declining && <DeclineSheet quote={q} onClose={() => setDeclining(false)} />}
    </section>
  );
}

function DeclineSheet({ quote: q, onClose }: { quote: Quote; onClose: () => void }) {
  const decline = useDeclineQuote(q.id);
  const toast = useToast();
  const [reason, setReason] = useState("");
  const cr = q.kind === "change_request";
  return (
    <Modal
      title={cr ? `Decline change request ${q.number}` : `Decline quote ${q.number}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger-solid"
            busy={decline.isPending}
            disabled={!reason.trim()}
            onClick={() =>
              decline.mutate(reason.trim(), {
                onSuccess: () => {
                  toast("Declined. We've let CoreNode know.");
                  onClose();
                },
              })
            }
          >
            Decline
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">Tell us why, so we can follow up. CoreNode can send a revised version.</p>
        <Field label="Reason">
          <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {decline.error && <ErrorNote error={decline.error} />}
      </div>
    </Modal>
  );
}
